import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
} from '@mui/material';
import { ProcedureStatus } from '../../types/procedures';

// Order statuses for the orders table
const ORDER_STATUSES = [
  'Saved as Draft',
  ...Object.values(ProcedureStatus).filter(status => status !== 'Saved as Draft')
];

// Utility function to safely format dates (handles Firebase Timestamps and regular dates)
const formatOrderDate = (dateValue) => {
  if (!dateValue) return '';
  
  try {
    // Handle Firebase Timestamp objects
    if (dateValue?.toDate) {
      return dateValue.toDate().toLocaleDateString();
    }
    
    // Handle regular Date objects or date strings
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date value:', dateValue);
      return 'Invalid Date';
    }
    
    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting date:', error, dateValue);
    return 'Invalid Date';
  }
};

const OrdersTableWithTabs = ({ orders, loading, onSendOrder }) => {
  const [tab, setTab] = useState(0);
  const status = ORDER_STATUSES[tab];
  const filtered = orders.filter(order => order.status === status);
  const draftOrdersCount = orders.filter(order => order.status === 'Saved as Draft').length;

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, newValue) => setTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1 }}
        TabIndicatorProps={{ style: { height: 2 } }}
      >
        {ORDER_STATUSES.map((s, i) => {
          const count = orders.filter(order => order.status === s).length;
          const isDraftTab = s === 'Saved as Draft';
          const hasNewDrafts = isDraftTab && count > 0;
          
          return (
            <Tab 
              key={s} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {s}
                  {count > 0 && (
                    <Chip 
                      label={count}
                      size="small"
                      color={isDraftTab ? "warning" : "default"}
                      sx={{ 
                        fontSize: '0.7rem', 
                        height: '18px',
                        minWidth: '18px',
                        animation: hasNewDrafts ? 'flash 1.5s infinite' : 'none',
                        '@keyframes flash': {
                          '0%, 50%': { opacity: 1 },
                          '25%, 75%': { opacity: 0.5 },
                        }
                      }}
                    />
                  )}
                </Box>
              }
              sx={{ minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '0.875rem' }} 
            />
          );
        })}
      </Tabs>
      <TableContainer>
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, fontSize: '0.875rem' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
              {status === 'Saved as Draft' && (
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={status === 'Saved as Draft' ? 5 : 4} align="center" sx={{ py: 1 }}>Loading orders...</TableCell>
              </TableRow>
            ) : filtered.length > 0 ? (
              filtered.map(order => (
                <TableRow key={order.id}>
                  <TableCell>{formatOrderDate(order.orderDate)}</TableCell>
                  <TableCell>{order.testType}</TableCell>
                  <TableCell>{order.description || ''}</TableCell>
                  <TableCell>{order.orderingProvider || ''}</TableCell>
                  {status === 'Saved as Draft' && (
                    <TableCell>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => {
                          if (onSendOrder) {
                            onSendOrder(order);
                          }
                        }}
                        sx={{ 
                          fontSize: '0.7rem',
                          minWidth: 'auto',
                          px: 1,
                        }}
                      >
                        Send Order
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={status === 'Saved as Draft' ? 5 : 4} align="center" sx={{ py: 1, fontStyle: 'italic', color: 'text.secondary' }}>No orders with status "{status}".</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default OrdersTableWithTabs; 